import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Bell, Terminal, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSession } from '@/contexts/session-context';
import { useWorkspace } from '@/contexts/workspace-context';
import { useCurrentRole } from '@/hooks/use-current-role';
import { usageAlertTier } from '@/lib/usage-alert';
import { orgUrl } from '@/lib/org-url';
import { isValidPublicId } from '@/lib/public-id';
import { useSidebarOrgUsageQuery } from '@/components/billing/queries';
import { BootstrapCodeDialog } from '@/pages/sandbox/bootstrap-code-dialog';

const CLI_DISMISSED_LS_KEY = 'hookmyapp:notifications:cli-dismissed';

/**
 * Header bell + notifications panel. Surfaces things the user can act on:
 *   - "Install the CLI" onboarding nudge (gated on `cliLastSeenAt === null`)
 *   - org-pooled usage alert when the org is approaching/at its limit
 *
 * The usage entry persists here whenever the org pool is at/over 75% — it is
 * the durable record of the alert, independent of the top-of-page banner's
 * per-tier dismissal (dismissing the banner never silences the bell). Admins
 * get an Upgrade Plan action; members get a read-only explanation (spec D5).
 *
 * CLI dismissal is local (`localStorage`); once the user actually uses the
 * CLI, `cliLastSeenAt` flips on the server and the nudge stays gone.
 */
export function HeaderBell() {
  const { user } = useSession();
  const { orgUsage, channels, activeWorkspace } = useWorkspace();
  const { role } = useCurrentRole();
  const isAdmin = role === 'admin';

  const cliPending = user?.cliLastSeenAt === null;
  const [cliDismissed, setCliDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(CLI_DISMISSED_LS_KEY) === '1';
  });

  const showCli = cliPending && !cliDismissed;

  // AIT-132: the alert reflects the org being VIEWED — the URL's org on org
  // routes, else the active workspace's org. The shell's context orgUsage is
  // keyed to the active workspace's org, so it only serves the matching case;
  // viewing another org (or having no active workspace) fetches its own
  // fail-soft copy, cache-shared with the org sidebar's query.
  const { orgId: urlOrgId } = useParams<{ orgId?: string }>();
  const wsOrgId = activeWorkspace?.organizationPublicId ?? null;
  const viewedOrgId = urlOrgId && isValidPublicId(urlOrgId, 'org') ? urlOrgId : wsOrgId;
  const needsOwnFetch = viewedOrgId !== null && viewedOrgId !== wsOrgId;
  const ownUsageQuery = useSidebarOrgUsageQuery(needsOwnFetch ? viewedOrgId : null);
  const usage = needsOwnFetch ? (ownUsageQuery.data ?? null) : orgUsage;

  // Channels are workspace-scoped, so the frozen signal only applies when the
  // viewed org IS the active workspace's org.
  const frozen =
    viewedOrgId === wsOrgId &&
    channels.some((c) => c.forwardingDisabledReason === 'usage_limit');
  const percentage = usage?.percentage ?? 0;
  // AIT-420: a v2 org's meter is actions, not messages.
  const onActions = usage?.usageUnit === 'actions';
  const usageTier = usageAlertTier(percentage, frozen);
  const showUsage = usageTier > 0;
  const orgId = viewedOrgId;

  const count = (showCli ? 1 : 0) + (showUsage ? 1 : 0);
  const hasNotifications = count > 0;

  function dismissCli(): void {
    window.localStorage.setItem(CLI_DISMISSED_LS_KEY, '1');
    setCliDismissed(true);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            aria-label="Notifications"
            className="relative"
          >
            <Bell className="h-5 w-5" />
            {hasNotifications && (
              <span
                aria-label={`${count} pending notification${count === 1 ? '' : 's'}`}
                className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-background"
              />
            )}
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">Notifications</span>
        </div>
        {hasNotifications ? (
          <div className="flex flex-col gap-2 p-2">
            {showUsage && (
              <div className="rounded-md border bg-card p-3">
                <div className="flex items-start gap-2">
                  <TriangleAlert
                    className={`mt-0.5 size-4 shrink-0 ${frozen ? 'text-destructive' : 'text-amber-500'}`}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {frozen
                        ? onActions
                          ? 'Action limit reached'
                          : 'Message limit reached'
                        : onActions
                          ? 'Approaching action limit'
                          : 'Approaching message limit'}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {frozen
                        ? onActions
                          ? 'Your organization has used every action in this plan. Your channels are paused.'
                          : 'Your organization reached its monthly message limit. Messages are no longer being sent or received.'
                        : onActions
                          ? `Your organization has used ${percentage}% of this month's actions.`
                          : `Your organization is at ${percentage}% of its monthly message limit.`}
                      {!isAdmin && ' Contact your admin.'}
                    </p>
                    {isAdmin && orgId && (
                      <div className="mt-3">
                        <Button
                          size="sm"
                          render={<Link to={orgUrl(orgId, 'billing')} />}
                        >
                          Upgrade Plan
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {showCli && (
              <div className="rounded-md border bg-card p-3">
                <div className="flex items-start gap-2">
                  <Terminal className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      Set up with your AI agent
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Give Claude Code, Codex, or any AI agent everything it
                      needs to install and connect HookMyApp for you.
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <BootstrapCodeDialog
                        variant="dashboard"
                        trigger={
                          <Button size="sm">Install</Button>
                        }
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={dismissCli}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            You&apos;re all caught up.
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
